// The in-memory API's DE briefs (DE-BRIEFS; services/api/src/domain/briefs/settings.ts): the
// rhythm at three levels — the company's defaults (with its Chief of Staff), a person's for the
// briefs they receive, a DE's with its recipients — each storing only what it changes (null
// inherits). Resolution as the API's `resolveSchedule`: company over the defaults; person over
// the company; a DE over its first recipient's person level over the company; off anywhere wins.
// No sample data: every level starts empty (the defaults apply); a DE's recipients default to the
// person its seat reports to when the twin knows them, else none.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { json, notFound, problem, validation } from "./fake";
import type { FakeDe } from "./fake-data";

type View = OperationOutput<"briefSettings.getCompany">;
type Schedule = View["effective"];
type Fields = View["setting"];
type Put = OperationInput<"briefSettings.putCompany">["body"];
type RulesView = OperationOutput<"briefRules.list">;
type RulePut = OperationInput<"briefRules.put">["body"];
type Person = { membership_id: string; name: string };

/** services/api/src/domain/briefs/schedule.ts `BRIEF_DEFAULTS`. */
export const FAKE_BRIEF_DEFAULTS: Schedule = {
  enabled: true,
  frequency: "daily",
  days: [1, 2, 3, 4, 5],
  time: "08:00",
};

/** The routes of this twin (all `feature: agents`). */
export const FAKE_BRIEFS_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "briefSettings.getCompany",
  "briefSettings.putCompany",
  "briefSettings.getMine",
  "briefSettings.putMine",
  "briefSettings.getDe",
  "briefSettings.putDe",
]);

/** COS-BRIEF-RULES: the rules the twin serves (the CoS's agent routes are the API's only). */
export const FAKE_BRIEF_RULES_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "briefRules.list",
  "briefRules.put",
  "briefRules.delete",
]);

const EMPTY: Fields = { enabled: null, frequency: null, days: null, time: null };

export function resolveBriefSchedule(levels: readonly (Fields | null | undefined)[]): Schedule {
  let out: Schedule = { ...FAKE_BRIEF_DEFAULTS, days: [...FAKE_BRIEF_DEFAULTS.days] };
  for (const l of levels) {
    if (!l) continue;
    out = {
      enabled: out.enabled && l.enabled !== false,
      frequency: l.frequency ?? out.frequency,
      days: l.days?.length ? [...l.days] : out.days,
      time: l.time ?? out.time,
    };
  }
  return out;
}

function daysOf(s: Schedule): number[] {
  if (s.frequency === "daily") return [1, 2, 3, 4, 5];
  if (s.frequency === "weekly") return [s.days[0] ?? 1];
  return [...new Set(s.days)].sort((a, b) => a - b);
}

/** A zone's wall clock at an instant: year, month, day, ISO weekday, minutes. */
function wall(at: Date, zone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return { y: n("year"), m: n("month"), d: n("day"), h: n("hour"), min: n("minute") };
}

/** The instant a zone's wall clock reads y-m-d h:min (one correction pass; DST-close enough). */
function zoned(y: number, m: number, d: number, h: number, min: number, zone: string): Date {
  const guess = Date.UTC(y, m - 1, d, h, min);
  const w = wall(new Date(guess), zone);
  const offset = Date.UTC(w.y, w.m - 1, w.d, w.h, w.min) - guess;
  return new Date(guess - offset);
}

/** The first slot after `now` (as the API's `nextSlot`); null when off. */
export function nextBriefAt(s: Schedule, zone: string, now: Date): Date | null {
  if (!s.enabled) return null;
  const days = new Set(daysOf(s));
  const [hh, mm] = s.time.split(":").map(Number);
  const today = wall(now, zone);
  for (let i = 0; i <= 8; i++) {
    const day = new Date(Date.UTC(today.y, today.m - 1, today.d + i));
    const iso = day.getUTCDay() === 0 ? 7 : day.getUTCDay();
    if (!days.has(iso)) continue;
    const at = zoned(
      day.getUTCFullYear(),
      day.getUTCMonth() + 1,
      day.getUTCDate(),
      hh ?? 8,
      mm ?? 0,
      zone,
    );
    if (at.getTime() > now.getTime()) return at;
  }
  return null;
}

type DeRow = Fields & { recipients: string[] | null };
type CompanyRow = Fields & { cos: string | null };

export function createBriefsFake(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  /** The company's active people. */
  peopleOf: (companyId: string) => Person[];
  /** The CEO the CoS report goes to (the twin: the owner). */
  ceoOf: (companyId: string) => string | null;
  timezoneOf: (companyId: string) => string;
}) {
  const company = new Map<string, CompanyRow>();
  const person = new Map<string, Fields>();
  const de = new Map<string, DeRow>();
  const key = (co: string, id: string) => `${co}:${id}`;

  const selfOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person") ?? null;
  const fieldsOf = (r: Fields | null | undefined): Fields =>
    r ? { enabled: r.enabled, frequency: r.frequency, days: r.days, time: r.time } : EMPTY;
  const namesOf = (co: string, ids: readonly string[]): Person[] => {
    const people = deps.peopleOf(co);
    return ids.flatMap((id) => people.filter((x) => x.membership_id === id));
  };
  const activeDes = (co: string) =>
    deps
      .desOf(co)
      .filter((d) => !d.removed_at && d.status !== "retired")
      .map((d) => ({ membership_id: d.membership_id, name: d.display_name }));

  function view(
    p: Principal,
    v: {
      scope: View["scope"];
      subject: string | null;
      row: Fields | null | undefined;
      inherited: Schedule;
      effective: Schedule;
      recipients?: { ids: string[]; set: boolean };
      cos?: View["cos"];
      canEdit: boolean;
    },
  ): View {
    const co = p.company.id;
    const zone = deps.timezoneOf(co);
    const next = nextBriefAt(v.effective, zone, deps.now());
    return {
      scope: v.scope,
      subject_id: v.subject,
      setting: fieldsOf(v.row),
      inherited: v.inherited,
      effective: v.effective,
      time_zone: zone,
      next_at: next ? next.toISOString() : null,
      recipients: v.recipients ? namesOf(co, v.recipients.ids) : [],
      recipients_set: v.recipients?.set ?? false,
      recipient_choices: v.scope === "de" ? deps.peopleOf(co) : [],
      recipient_schedules: [],
      cos: v.cos ?? null,
      can_edit: v.canEdit,
    };
  }

  function companyView(p: Principal): View {
    const co = p.company.id;
    const row = company.get(co);
    const ceo = deps.ceoOf(co);
    return view(p, {
      scope: "company",
      subject: null,
      row,
      inherited: FAKE_BRIEF_DEFAULTS,
      effective: resolveBriefSchedule([row]),
      cos: {
        membership_id: row?.cos ?? null,
        set: Boolean(row?.cos),
        ceo: ceo ? (namesOf(co, [ceo])[0] ?? null) : null,
        choices: activeDes(co),
      },
      canEdit: p.manager,
    });
  }

  function personView(p: Principal, me: string): View {
    const co = p.company.id;
    const crow = company.get(co);
    const row = person.get(key(co, me));
    return view(p, {
      scope: "person",
      subject: me,
      row,
      inherited: resolveBriefSchedule([crow]),
      effective: resolveBriefSchedule([crow, row]),
      canEdit: true,
    });
  }

  const deOf = (co: string, id: string) =>
    deps.desOf(co).find((d) => d.membership_id === id && !d.removed_at) ?? null;

  function recipientsOf(co: string, d: FakeDe): { ids: string[]; set: boolean } {
    const chosen = de.get(key(co, d.membership_id))?.recipients;
    if (chosen?.length) return { ids: chosen, set: true };
    const line = d.supervisor.membership_id;
    return {
      ids: deps.peopleOf(co).some((x) => x.membership_id === line) ? [line] : [],
      set: false,
    };
  }

  const mayEditDe = (p: Principal, d: FakeDe) =>
    p.manager || selfOf(p)?.membership_id === d.supervisor.membership_id;

  function deView(p: Principal, d: FakeDe): View {
    const co = p.company.id;
    const crow = company.get(co);
    const row = de.get(key(co, d.membership_id));
    const recipients = recipientsOf(co, d);
    const sole = recipients.ids.length === 1 ? recipients.ids[0] : undefined;
    const prow = sole ? person.get(key(co, sole)) : null;
    const v = view(p, {
      scope: "de",
      subject: d.membership_id,
      row,
      inherited: resolveBriefSchedule([crow, prow]),
      effective: resolveBriefSchedule([crow, prow, row]),
      recipients,
      canEdit: mayEditDe(p, d),
    });
    const each = v.recipients.map((r) => {
      const effective = resolveBriefSchedule([crow, person.get(key(co, r.membership_id)), row]);
      const next = nextBriefAt(effective, v.time_zone, deps.now());
      return { person: r, effective, next_at: next ? next.toISOString() : null };
    });
    const soonest = each
      .map((e) => e.next_at)
      .filter((x): x is string => x !== null)
      .sort()[0];
    return { ...v, recipient_schedules: each, next_at: soonest ?? null };
  }

  /** The body's rhythm fields, checked; a string is the validation message. */
  function fieldsFrom(body: unknown): Fields | string {
    const b = body as Partial<Put> | null;
    if (!b || !("enabled" in b) || !("frequency" in b) || !("days" in b) || !("time" in b))
      return "enabled, frequency, days and time are required (null inherits)";
    const days = b.days ? [...new Set(b.days)].sort((x, y) => x - y) : null;
    if (days && (days.length === 0 || days.some((x) => !Number.isInteger(x) || x < 1 || x > 7)))
      return "days: ISO weekdays 1 to 7";
    if (b.frequency === "weekly" && days && days.length !== 1) return "days: weekly takes one day";
    if (b.time !== null && b.time !== undefined && !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(b.time))
      return "time: HH:MM, 24-hour";
    return {
      enabled: b.enabled ?? null,
      frequency: b.frequency ?? null,
      days,
      time: b.time ?? null,
    };
  }

  // COS-BRIEF-RULES: one rule per recipient. The twin's owner is its CEO (`ceoOf`), so owner
  // rules are refused (422) as the API refuses them while the owner is the CEO.
  const rules = new Map<string, RulesView["rules"][number]>();
  const rulesOf = (co: string) =>
    [...rules.entries()].filter(([k]) => k.startsWith(`${co}:`)).map(([, r]) => r);
  const mayEditRules = (p: Principal) =>
    p.manager || selfOf(p)?.membership_id === deps.ceoOf(p.company.id);
  function rulesView(p: Principal): RulesView {
    const co = p.company.id;
    const ceo = deps.ceoOf(co);
    const ceoP = ceo ? (namesOf(co, [ceo])[0] ?? null) : null;
    const canEdit = mayEditRules(p);
    const cos = company.get(co)?.cos ?? null;
    return {
      rules: canEdit ? rulesOf(co) : [],
      ceo: ceoP,
      owner: ceoP,
      owner_is_ceo: true,
      cos: cos ? (activeDes(co).find((d) => d.membership_id === cos) ?? null) : null,
      time_zone: deps.timezoneOf(co),
      can_edit: canEdit,
      recipient_choices: canEdit ? deps.peopleOf(co) : [],
    };
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "briefRules.list": ({ principal }) => {
      if (!principal) return notFound();
      return json(rulesView(principal));
    },
    "briefRules.put": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!mayEditRules(principal)) return problem(403, "forbidden", "Forbidden");
      const co = principal.company.id;
      const b = body as RulePut;
      const to = b.recipient;
      if (to?.kind !== "person")
        return validation("recipient: the owner is the CEO: the CEO report already reaches them");
      const who = deps.peopleOf(co).find((x) => x.membership_id === to.membership_id);
      if (!who) return validation("recipient: not an active person of this company");
      const k = key(co, who.membership_id);
      const cur = rules.get(k);
      const frequency = b.frequency ?? cur?.frequency ?? "weekly";
      const days =
        frequency === "daily"
          ? null
          : b.days !== undefined
            ? b.days
            : (cur?.days ?? (frequency === "weekly" ? [5] : null));
      if (frequency === "weekly" && days && days.length !== 1)
        return validation("days: weekly takes one day");
      if (frequency === "custom" && !days?.length) return validation("days: custom takes a day");
      rules.set(k, {
        id: cur?.id ?? crypto.randomUUID(),
        recipient: { kind: "person", membership_id: who.membership_id, name: who.name },
        sections: b.sections ?? cur?.sections ?? ["work", "kpis", "stuck", "decisions"],
        enabled: b.enabled ?? cur?.enabled ?? true,
        frequency,
        days,
        time: b.time ?? cur?.time ?? "08:00",
        channel: b.channel ?? cur?.channel ?? "dm",
        applies: b.enabled ?? cur?.enabled ?? true,
        note: null,
        next_at: null,
        instruction_message_id: null,
        updated_at: deps.now().toISOString(),
      });
      return json(rulesView(principal));
    },
    "briefRules.delete": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!mayEditRules(principal)) return problem(403, "forbidden", "Forbidden");
      const hit = [...rules.entries()].find(
        ([k, r]) => k.startsWith(`${principal.company.id}:`) && r.id === params.id,
      );
      if (!hit) return notFound();
      rules.delete(hit[0]);
      return json(rulesView(principal));
    },
    "briefSettings.getCompany": ({ principal }) => {
      if (!principal) return notFound();
      return json(companyView(principal));
    },
    "briefSettings.putCompany": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const f = fieldsFrom(body);
      if (typeof f === "string") return validation(f);
      const b = body as Put;
      if (b.recipients != null) return validation("recipients: recipients are set per DE");
      // Left out: the stored choice stays (as the API).
      const cos =
        b.cos_membership_id === undefined
          ? (company.get(principal.company.id)?.cos ?? null)
          : b.cos_membership_id;
      if (cos && !activeDes(principal.company.id).some((d) => d.membership_id === cos))
        return validation("cos_membership_id: not an active digital employee of this company");
      company.set(principal.company.id, { ...f, cos });
      return json(companyView(principal));
    },
    "briefSettings.getMine": ({ principal }) => {
      if (!principal) return notFound();
      const me = selfOf(principal);
      if (!me) return problem(403, "forbidden", "Forbidden");
      return json(personView(principal, me.membership_id));
    },
    "briefSettings.putMine": ({ principal, body }) => {
      if (!principal) return notFound();
      const me = selfOf(principal);
      if (!me) return problem(403, "forbidden", "Forbidden");
      const f = fieldsFrom(body);
      if (typeof f === "string") return validation(f);
      const b = body as Put;
      if (b.recipients != null) return validation("recipients: recipients are set per DE");
      if (b.cos_membership_id != null)
        return validation("cos_membership_id: the Chief of Staff is the company's setting");
      person.set(key(principal.company.id, me.membership_id), f);
      return json(personView(principal, me.membership_id));
    },
    "briefSettings.getDe": ({ principal, params }) => {
      if (!principal) return notFound();
      const d = deOf(principal.company.id, params.id ?? "");
      if (!d) return notFound();
      return json(deView(principal, d));
    },
    "briefSettings.putDe": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const co = principal.company.id;
      const d = deOf(co, params.id ?? "");
      if (!d) return notFound();
      if (!mayEditDe(principal, d)) return problem(403, "forbidden", "Forbidden");
      const f = fieldsFrom(body);
      if (typeof f === "string") return validation(f);
      const b = body as Put;
      if (b.cos_membership_id != null)
        return validation("cos_membership_id: the Chief of Staff is the company's setting");
      const recipients =
        b.recipients === undefined
          ? (de.get(key(co, d.membership_id))?.recipients ?? null)
          : b.recipients
            ? [...new Set(b.recipients)]
            : null;
      if (b.recipients && recipients) {
        if (recipients.length === 0)
          return validation("recipients: name at least one person, or null");
        const live = new Set(deps.peopleOf(co).map((x) => x.membership_id));
        const bad = recipients.find((r) => !live.has(r));
        if (bad) return validation(`recipients: ${bad} is not an active person of this company`);
      }
      de.set(key(co, d.membership_id), { ...f, recipients });
      return json(deView(principal, d));
    },
  };

  return { handlers };
}
